import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getIsAuth, getIsAuthPending } from 'redux/reducers/auth/selectors';
import { authManager } from 'services/auth';
import { IS_NEMAN } from 'services/constants/env';

/**
 * Guards a route, and is the only place allowed to decide what happens to an unauthenticated one.
 *
 * Pages used to fall back to rendering <LoginPage /> while the session was still being established.
 * That screen has no idea which page it stood in for, so when the silent check failed it sent the
 * user to Keycloak with the default destination and they came back somewhere else entirely.
 *
 * Returns false while the answer is still unknown — render a spinner, not a login screen.
 */
export const useRequireAuth = (): boolean => {
	const navigate = useNavigate();
	const ifAuth = useSelector(getIsAuth);
	const isPending = useSelector(getIsAuthPending);

	useEffect(() => {
		if (ifAuth || isPending) return;

		if (IS_NEMAN || authManager.isSignedOut()) {
			navigate('/login');
			return;
		}

		void authManager.loginRedirect(`${window.location.pathname}${window.location.search}`);
	}, [ifAuth, isPending, navigate]);

	return ifAuth;
};
